import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Apple, Share, Plus, Sparkles, ChevronRight, ArrowDown, MoreVertical, CheckCircle2 } from "lucide-react";

const InstallApp = () => {
  const [device, setDevice] = useState("ios");

  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (/Android/i.test(ua)) setDevice("android");
    else if (/iPhone|iPad|iPod/i.test(ua)) setDevice("ios");
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="install-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-sky-200 bg-sky-50 text-sky-700 text-xs tracking-[0.2em] uppercase font-bold mb-4">
          <Sparkles className="w-3.5 h-3.5" /> Installa EasyIce
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-light text-[#032B44]">
          Aggiungi <span className="text-[#00A3FF]">EasyIce</span> sul tuo telefono
        </h1>
        <p className="text-slate-600 mt-3 text-sm sm:text-base max-w-lg mx-auto">
          Ordina ghiaccio in 2 tap. Apri direttamente l'app dalla schermata home, senza dover digitare ogni volta l'indirizzo.
        </p>
      </div>

      <div className="bg-white border border-sky-100 rounded-2xl overflow-hidden shadow-sm mb-6">
        <div className="grid grid-cols-2 border-b border-sky-100">
          <button
            onClick={() => setDevice("ios")}
            className={`py-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              device === "ios" ? "bg-[#032B44] text-white" : "bg-white text-[#032B44] hover:bg-sky-50"
            }`}
            data-testid="tab-ios"
          >
            <Apple className="w-4 h-4" /> iPhone / iPad
          </button>
          <button
            onClick={() => setDevice("android")}
            className={`py-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              device === "android" ? "bg-[#032B44] text-white" : "bg-white text-[#032B44] hover:bg-sky-50"
            }`}
            data-testid="tab-android"
          >
            <Smartphone className="w-4 h-4" /> Android
          </button>
        </div>

        <div className="p-6 sm:p-8">
          {device === "ios" ? <IosSteps /> : <AndroidSteps />}
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#032B44] to-[#00A3FF] rounded-2xl p-6 text-white text-center">
        <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-sky-200" />
        <p className="font-heading text-lg">Pronto in 30 secondi</p>
        <p className="text-sm opacity-80 mt-1">L'icona blu di EasyIce apparirà sul tuo telefono.</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-5 px-5 h-10 rounded-lg bg-white text-[#032B44] text-sm font-medium hover:bg-sky-50 transition-colors"
          data-testid="install-back-home"
        >
          Vai al sito <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

const Step = ({ n, title, children, icon }) => (
  <div className="flex items-start gap-4 mb-5 last:mb-0" data-testid={`install-step-${n}`}>
    <div className="w-9 h-9 rounded-full bg-[#032B44] text-white grid place-items-center font-heading font-medium shrink-0">
      {n}
    </div>
    <div className="flex-1 pt-1">
      <p className="text-sm font-semibold text-[#032B44] mb-1 flex items-center gap-2 flex-wrap">
        {title}
        {icon && <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-sky-50 border border-sky-200 text-sky-700">{icon}</span>}
      </p>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  </div>
);

const IosSteps = () => (
  <div>
    <Step n={1} title="Apri Safari" icon={<Apple className="w-4 h-4" />}>
      Importante: l'app si installa <strong>solo da Safari</strong>, non da Chrome o altri browser.
    </Step>
    <Step n={2} title="Tocca il pulsante Condividi" icon={<Share className="w-4 h-4" />}>
      In basso al centro della schermata: l'icona quadrata con la freccia rivolta verso l'alto.
    </Step>
    <Step n={3} title='Scorri e tocca "Aggiungi a Home"' icon={<Plus className="w-4 h-4" />}>
      Scorri il menu fino a trovare la voce <strong>"Aggiungi a Home"</strong> (o "Add to Home Screen").
    </Step>
    <Step n={4} title='Conferma con "Aggiungi"'>
      Vedi il nome <strong>EasyIce</strong> e l'icona blu in anteprima. Tocca "Aggiungi" in alto a destra.
    </Step>
    <Step n={5} title="Trovi l'icona sulla schermata home">
      Toccala per aprire EasyIce come una vera app. 🎉
    </Step>
  </div>
);

const AndroidSteps = () => (
  <div>
    <Step n={1} title="Apri Chrome" icon={<Smartphone className="w-4 h-4" />}>
      Sui dispositivi Android l'installazione funziona meglio da Google Chrome.
    </Step>
    <Step n={2} title="Tocca il menu (⋮) in alto a destra" icon={<MoreVertical className="w-4 h-4" />}>
      I tre puntini verticali nell'angolo della barra Chrome.
    </Step>
    <Step n={3} title='Tocca "Installa app" o "Aggiungi a schermata Home"' icon={<ArrowDown className="w-4 h-4" />}>
      A volte Chrome mostra direttamente un banner in basso "Installa EasyIce": tocca <strong>Installa</strong>.
    </Step>
    <Step n={4} title="Conferma con Installa">
      Vedi il nome <strong>EasyIce</strong> e l'icona. Tocca <strong>Installa</strong>.
    </Step>
    <Step n={5} title="L'icona compare tra le tue app">
      Apri EasyIce come una qualsiasi app. 🎉
    </Step>
  </div>
);

export default InstallApp;
